import { BrandCode } from './curriculum';

export type PlanDetailsSection = {
  title: string;
  description: string;
  contents: string[];
};

export type Plan =
  | 'kids'
  | 'preschool'
  | 'students'
  | 'adult'
  | 'preschool and kids'
  | 'kids and students'
  | 'preschool, kids and students';

export const planDetails: Partial<Record<BrandCode, Partial<Record<Plan, PlanDetailsSection[]>>>> = {
  BSFL: {
    kids: [
      {
        title: 'Basic Plan Details',
        description: 'Contains essentials for 1st-6th Grade weekly group ministry:',
        contents: [
          'Leader Audio Training',
          'Leader Guide PDFs',
          'Kids Activity Page PDFs',
          'Special Buddies Resources',
          'Visual Aids PDFs (Games, Teaching Pictures, Maps, etc.)',
          'Additional Printables',
          'Coloring Page PDFs',
          'Bible Story Videos',
          'Life Action Videos',
          'Missions Video',
          'Bible Story Time Videos (Special Buddies)',
          'Session-related Songs',
          'PowerPoint Presentation',
          'At Home Resources for Family Worship',
        ],
      },
      {
        title: 'Plus Plan Details',
        description: 'Everything in the Basic plan PLUS all you need for an extra hour:',
        contents: [
          'Worship Guide PDFs & RTFs',
          'Huddle-ups',
          'Optional Bible Stories',
          'Decorating Ideas',
          'Worship Videos and Audio',
          'Slides',
          'Lead Sheets',
          'Countdown Videos',
        ],
      },
    ],
    preschool: [
      {
        title: 'Basic Plan Details',
        description: 'Contains essentials for Babies - Kindergarten weekly group ministry:',
        contents: [
          'Leader Audio Training',
          'Leader Guide PDFs',
          'Preschool Activity Page PDFs',
          'Bonus Teaching Hour PDFs',
          'Visual Aids PDFs (Games, Teaching Pictures, Maps, etc.)',
          'Additional Printables',
          'Coloring Page PDFs',
          'Bible Story Time Videos',
          'Mission Videos',
          'Session-related Songs',
          'PowerPoint Presentations',
          'At Home Resources for Family Worship',
        ],
      },
      {
        title: 'Plus Plan Details',
        description: 'Everything in the Basic plan PLUS all you need for an extra hour:',
        contents: [
          'Worship Guide PDFs & RTFs',
          'Huddle-ups',
          'Decorating Ideas',
          'Worship Videos and Audio',
          'Slides',
          'Lead Sheets',
          'Countdown Videos',
        ],
      },
    ],
    students: [
      {
        title: 'Basic Plan Details',
        description: 'Contains essentials for weekly group ministry:',
        contents: ['Leader Guide PDFs', 'Daily Discipleship Guide PDFs', 'CSB, ESV, and KJV Options'],
      },
      {
        title: 'Plus Plan Details',
        description: 'Everything in the Basic plan PLUS:',
        contents: [
          'Middle School Leader Guide PDFs',
          'Middle School Daily Discipleship Guide PDFs',
          'One Sheet Printables',
          'Family Connection Printables',
          'Midweek Plans',
          'Visual Aids (Poster, Social Media Share Squares, etc.)',
          'PowerPoint Templates',
        ],
      },
    ],
    adult: [
      {
        title: 'Basic Plan Details',
        description: 'Contains essentials for weekly group ministry:',
        contents: [
          'Leader Audio Training',
          'Leader Guide PDFs for Adults',
          'Personal Study Guide PDFs for Adults',
          'Daily Discipleship Guide Leader and Learner PDFs',
          'CSB and KJV Options',
        ],
      },
      {
        title: 'Plus Plan Details',
        description: 'Everything in the Basic plan PLUS:',
        contents: [
          'NIV options for Basic materials',
          'Leader Guide Word Documents',
          'Visual Aids (Charts, Maps, etc.)',
          'Quicksource and Teacher Commentary',
          'Promo Videos',
          'Social Media Messages',
          'Family Connection Printables',
        ],
      },
    ],
    'preschool and kids': [
      {
        title: 'Basic Plan Details',
        description: 'Contains essentials for weekly group ministry:',
        contents: [
          'Leader Audio Training',
          'Leader Guide PDFs',
          'Kids Activity Page PDFs',
          'Special Buddies Resources',
          'Visual Aids PDFs (Games, Teaching Pictures, Maps, etc.)',
          'Additional Printables',
          'Coloring Page PDFs',
          'Bible Story Videos',
          'Life Action Videos',
          'Missions Video',
          'Bible Story Time Videos (Special Buddies)',
          'Session-related Songs',
          'PowerPoint Presentation',
          'At Home Resources for Family Worship',
          'Preschool Activity Page PDFs',
          'Bonus Teaching Hour PDFs',
          'Bible Story Time Videos',
          'Mission Videos',
          'PowerPoint Presentations',
        ],
      },
      {
        title: 'Plus Plan Details',
        description: 'Everything in the Basic plan PLUS all you need for an extra hour:',
        contents: [
          'Worship Guide PDFs & RTFs',
          'Huddle-ups',
          'Optional Bible Stories',
          'Decorating Ideas',
          'Worship Videos and Audio',
          'Slides',
          'Lead Sheets',
          'Countdown Videos',
        ],
      },
    ],
  },
  ETB: {
    kids: [
      {
        title: 'Basic Plan Details',
        description: 'Contains essentials for 1st-6th Grade weekly group ministry:',
        contents: [
          'Leader Audio Training',
          'Leader Guide PDFs',
          'Explorer Guide PDFs',
          'Explore the Bible on Location Videos',
          'Music Videos and Audio',
          'PowerPoint Presentations',
          'Visual Aids (Charts, Maps, etc.)',
          'At Home Resources for Family Worship',
        ],
      },
      {
        title: 'Plus Plan Details',
        description: 'Everything in the Basic plan PLUS all you need for an extra hour:',
        contents: ['Worship Guide PDFs', 'Worship Videos and Audio', 'Music and Missions Videos', 'Slides'],
      },
    ],
    preschool: [
      {
        title: 'Basic Plan Details',
        description: 'Contains essentials for Babies - Preschool weekly group ministry:',
        contents: [
          'Leader Audio Training',
          'Leader Guide PDFs',
          'Diaper Bag Card PDFs',
          'Activity Page PDFs',
          'Music Audio',
          'PowerPoint Presentations',
          'Visual Aids (Teaching Pictures, Books, etc.)',
          'At Home Resources for Family Worship',
        ],
      },
      {
        title: 'Plus Plan Details',
        description: 'Everything in the Basic plan PLUS all you need for an extra hour:',
        contents: ['Worship Guide PDFs', 'Music and Missions Videos'],
      },
    ],
    students: [
      {
        title: 'Basic Plan Details',
        description: 'Contains essentials for weekly group ministry:',
        contents: [
          'Leader Training Documents',
          'Leader Guide PDFs',
          'Daily Discipleship Guide PDFs',
          'CSB, ESV, KJV, and NIV Options',
        ],
      },
      {
        title: 'Plus Plan Details',
        description: 'Everything in the Basic plan PLUS:',
        contents: [
          'Middle School Leader Guide PDFs (CSB)',
          'Middle School Daily Discipleship Guide PDFs (CSB)',
          'Daily Bible Reading Plan',
          'Animated Videos',
          'One Sheet Printables',
          'Family Connection Printables',
          'Midweek Plans',
          'Visual Aids (Social Media Share Squares, Charts, Maps, etc.)',
          'PowerPoint Templates',
        ],
      },
    ],
    adult: [
      {
        title: 'Basic Plan Details',
        description: 'Contains essentials for weekly group ministry:',
        contents: [
          'Leader Guide PDFs',
          'Personal Study Guide PDFs',
          'Daily Discipleship Guide Leader and Learner PDFs',
          'CSB and KJV Options',
        ],
      },
      {
        title: 'Plus Plan Details',
        description: 'Everything in the Basic plan PLUS:',
        contents: [
          'NIV and ESV options for Basic materials',
          'Group Plans Word Documents',
          'Visual Aids (Charts, Maps, etc.)',
          'QuickSource Leader Guides',
          'Adult Commentary',
          'Social Media Messages',
          'Memory Verse Masters',
        ],
      },
    ],
    'preschool and kids': [
      {
        title: 'Basic Plan Details',
        description: 'Contains essentials for weekly group ministry:',
        contents: [
          'Leader Audio Training',
          'Leader Guide PDFs',
          'Explorer Guide PDFs',
          'Explore the Bible on Location Videos',
          'Music Videos and Audio',
          'PowerPoint Presentations',
          'Visual Aids (Charts, Maps, etc.)',
          'At Home Resources for Family Worship',
          'Diaper Bag Card PDFs',
          'Activity Page PDFs',
          'Music Audio',
          'Visual Aids (Teaching Pictures, Books, etc.)',
        ],
      },
      {
        title: 'Plus Plan Details',
        description: 'Everything in the Basic plan PLUS all you need for an extra hour:',
        contents: ['Worship Guide PDFs', 'Worship Videos and Audio', 'Music and Missions Videos', 'Slides'],
      },
    ],
  },
  TGP: {
    kids: [
      {
        title: 'Basic Plan Details',
        description: 'Contains essentials for weekly group ministry:',
        contents: [
          'Leader Audio Training',
          'Leader Guide PDFs and RTFs',
          'Activity Page PDFs',
          'Activity Printables',
          'Big Picture Cards',
          'Preteen Learner Guide PDFs',
          'Bible Story Pictures',
          'Big Picture Question & Answer Posters',
          'Bible Story Videos',
          'Questions from Kids Videos',
          'Key Passage Songs Videos and Audio',
          'Missions Videos',
          'Volume Leader Resources',
          'Presentation Files',
          'At Home Resources for Family Worship',
        ],
      },
      {
        title: 'Plus Plan Details',
        description: 'Everything in the Basic plan PLUS all you need for an extra hour:',
        contents: [
          'Worship Guide PDFs and RTFs',
          'Midweek Guide PDFs and RTFs',
          'Worship Activity Printables',
          'Midweek Activity Printables',
          'Worship Music Videos and Audio',
        ],
      },
    ],
    preschool: [
      {
        title: 'Basic Plan Details',
        description: 'Contains essentials for Babies - Preschool group ministry:',
        contents: [
          'Leader Audio Training',
          'Leader Guide PDFs and RTFs',
          'Activity Page PDFs',
          'Activity Printables',
          'Big Picture Cards',
          'Bible Story Pictures',
          'Big Picture Question & Answer Posters',
          'Bible Story Videos',
          'Key Passage Songs Videos and Audio',
          'Missions Videos',
          'Volume Leader Resources',
          'Presentation Files',
          'At Home Resources for Family Worship',
        ],
      },
      {
        title: 'Plus Plan Details',
        description: 'Everything in the Basic plan PLUS all you need for an extra hour:',
        contents: ['Worship Guide PDFs and RTFs', 'Worship Music Videos and Audio'],
      },
    ],
    students: [
      {
        title: 'Basic Plan Details',
        description: 'Contains essentials for weekly group ministry:',
        contents: [
          'Leader Training Documents',
          'Leader Guide PDFs',
          'Daily Discipleship Guide PDFs',
          'CSB and ESV Options',
        ],
      },
      {
        title: 'Plus Plan Details',
        description: 'Everything in the Basic plan PLUS:',
        contents: [
          'Middle School Leader Guide PDFs',
          'Middle School Daily Discipleship Guide PDFs',
          'One Sheet Printables',
          'Family Connection Printables',
          'Midweek Plans',
          'Visual Aids (Posters, Social Media Share Squares, Charts, etc.)',
          'PowerPoint Templates',
        ],
      },
    ],
    adult: [
      {
        title: 'Basic Plan Details',
        description: 'Contains essentials for weekly group ministry:',
        contents: ['Leader Guide PDFs', 'Daily Discipleship Guide PDFs'],
      },
      {
        title: 'Plus Plan Details',
        description: 'Everything in the Basic plan PLUS:',
        contents: [
          'Leader Guide Word Documents',
          'Daily Discipleship Guide Word Documents',
          'Visual Aids (Charts, Maps, etc.)',
          'PowerPoint Resources',
          'Memory Verse Handouts',
        ],
      },
    ],
    'preschool and kids': [
      {
        title: 'Basic Plan Details',
        description: 'Contains essentials for weekly group ministry:',
        contents: [
          'Leader Audio Training',
          'Leader Guide PDFs and RTFs',
          'Activity Page PDFs',
          'Activity Printables',
          'Big Picture Cards',
          'Preteen Learner Guide PDFs',
          'Bible Story Pictures',
          'Big Picture Question & Answer Posters',
          'Bible Story Videos',
          'Questions from Kids Videos',
          'Key Passage Songs Videos and Audio',
          'Missions Videos',
          'Volume Leader Resources',
          'Presentation Files',
          'At Home Resources for Family Worship',
        ],
      },
      {
        title: 'Plus Plan Details',
        description: 'Everything in the Basic plan PLUS all you need for an extra hour:',
        contents: [
          'Worship Guide PDFs and RTFs',
          'Midweek Guide PDFs and RTFs',
          'Worship Activity Printables',
          'Midweek Activity Printables',
          'Worship Music Videos and Audio',
        ],
      },
    ],
  },
  YOU: {
    adult: [
      {
        title: 'Basic Plan Details',
        description: 'Contains essentials for weekly group ministry:',
        contents: ['Leader Guide PDFs for Adults', 'Personal Study Guide PDFs for Adults', 'CSB Bible Translation'],
      },
      {
        title: 'Plus Plan Details',
        description: 'Everything in the Basic plan PLUS:',
        contents: [
          'NIV options for Basic materials',
          'Leader Guide Word Documents',
          'Visual Aids (Charts, Maps, etc.)',
          'Quicksource and Teacher Commentary',
          'Social Media Messages',
        ],
      },
    ],
  },
  HYFI: {
    kids: [
      {
        title: 'Plan Details',
        description: 'Contains essentials for weekly group ministry:',
        contents: [
          'Leader Audio Training',
          'Interactive Digital Leader Guides and RTFs',
          'Activity Page PDFs',
          'Activity Printables',
          'Bible Story Videos',
          'Theme Song MP3 and Motions/Lyric Videos',
          'Countdown Videos',
          'Intro Videos',
          'Theme Verse Videos',
          'Monthly Leader Resources',
          'Monthly Family and Volunteer Resources',
          'Presentation Files',
          'Launch Resources',
        ],
      },
    ],
    preschool: [
      {
        title: 'Plan Details',
        description: 'Contains essentials for weekly group ministry:',
        contents: [
          'Leader Audio Training',
          'Interactive Digital Leader Guides and RTFs',
          'Activity Page PDFs',
          'Activity Printables',
          'Bible Story Videos',
          'Theme Song MP3 and Motions Video',
          'Countdown Videos',
          'Theme Verse Videos',
          'Monthly Leader Resources',
          'Monthly Family and Volunteer Resources',
          'Presentation Files',
          'Launch Resources',
        ],
      },
    ],
    students: [
      {
        title: 'Plan Details',
        description: 'Contains essentials for weekly group ministry:',
        contents: [
          'Leader Training',
          'Social Media Assets (Including Blank Slides)',
          'Digital Theme Posters',
          'Activity Printables',
          'Weekly Bible Story Videos',
          'Monthly Theme Videos',
          'Monthly Leader Resources',
          'Monthly Family and Volunteer Resources',
          'Presentation Files',
          'Launch Resources',
        ],
      },
    ],
    'preschool and kids': [
      {
        title: 'Plan Details',
        description: 'Contains essentials for weekly group ministry:',
        contents: [
          'Leader Audio Training',
          'Interactive Digital Leader Guides and RTFs',
          'Activity Page PDFs',
          'Activity Printables',
          'Bible Story Videos',
          'Theme Song MP3 and Motions Video',
          'Countdown Videos',
          'Theme Verse Videos',
          'Monthly Leader Resources',
          'Monthly Family and Volunteer Resources',
          'Theme Song MP3 and Motions/Lyric Videos',
          'Intro Videos',
          'Presentation Files',
          'Launch Resources',
        ],
      },
    ],
    'kids and students': [
      {
        title: 'Plan Details',
        description: 'Contains essentials for weekly group ministry:',
        contents: [
          'Leader Audio Training',
          'Leader Training',
          'Interactive Digital Leader Guides and RTFs',
          'Social Media Assets (Including Blank Slides)',
          'Activity Page PDFs',
          'Digital Theme Posters',
          'Activity Printables',
          'Bible Story Videos',
          'Weekly Bible Story Videos',
          'Theme Song MP3 and Motions/Lyric Videos',
          'Monthly Theme Videos',
          'Countdown Videos',
          'Intro Videos',
          'Theme Verse Videos',
          'Monthly Leader Resources',
          'Monthly Family and Volunteer Resources',
          'Presentation Files',
          'Launch Resources',
        ],
      },
    ],
    'preschool, kids and students': [
      {
        title: 'Plan Details',
        description: 'Contains essentials for weekly group ministry:',
        contents: [
          'Leader Audio Training',
          'Interactive Digital Leader Guides and RTFs',
          'Activity Page PDFs',
          'Activity Printables',
          'Bible Story Videos',
          'Theme Song MP3 and Motions Video',
          'Countdown Videos',
          'Theme Verse Videos',
          'Monthly Leader Resources',
          'Monthly Family and Volunteer Resources',
          'Theme Song MP3 and Motions/Lyric Videos',
          'Intro Videos',
          'Leader Training',
          'Social Media Assets (Including Blank Slides)',
          'Digital Theme Posters',
          'Weekly Bible Story Videos',
          'Monthly Theme Videos',
          'Presentation Files',
          'Launch Resources',
        ],
      },
    ],
  },
};
